import React, { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Button,
  TextField,
} from '@mui/material';
import MobilePagination from '../../rider/parcel/mobile.pagination';
import API from '../../../create/api';
import querystring from 'querystring';
import moment from 'moment';
import axios from 'axios';

const PICKUP_STATUS_OPTIONS = [
  { value: 'REQUESTED', label: 'Requested' },
  { value: 'RIDER_ASSIGNED', label: 'Rider Assigned' },
  { value: 'DONE', label: 'Done' },
  { value: 'CANCELED_BY_MERCHANT', label: 'Canceled by Merchant' },
];

export default function PickupRequestsPage() {
  const location = useLocation();
  const [pickupRequests, setPickupRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    date: '',
    status: '',
  });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sort, setSort] = useState({ _sort: 'id:desc' });

  function buildQuery() {
    const query: any = {
      _start: (page - 1) * limit,
      _limit: limit,
    };
    if (filters.date) {
      query.date = moment(filters.date).format('YYYY-MM-DD');
    }
    if (filters.status) {
      query.status = filters.status;
    }
    if (sort._sort) {
      query._sort = sort._sort;
    }
    return querystring.encode(query);
  }

  async function fetchAPI() {
    setLoading(true);
    const qs = buildQuery();

    try {
      const [requests, count] = await Promise.all([
        API.getAll(`${process.env.REACT_APP_API_SERVER}/pickup-requests?${qs}`),
        API.count(`${process.env.REACT_APP_API_SERVER}/pickup-requests/count?${qs}`),
      ]);
      setPickupRequests(requests as any[]);
      setTotalCount(Number(count));
    } catch (error) {}
    setLoading(false);
  }

  async function pickup_request_update(id: number, data: object) {
    try {
      await API.update(`${process.env.REACT_APP_API_SERVER}/pickup-requests`, id, data);

      window.location.href = `/dashboard/pickup-request`;
    } catch (error) {
      console.error('Error updating pickup request:', error);
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get('status');
    if (status) {
      setFilters((prevFilters) => ({ ...prevFilters, status }));
    }
  }, [location.search]); // Run effect when URL changes

  useEffect(() => {
    fetchAPI();
    // eslint-disable-next-line
  }, [filters, page, limit, sort]);

  const handlePrint = () => {
    window.print();
  };

  const handleSortChange = (field: string) => {
    setSort({
      _sort: field,
    });
  };

  const allSelected = useMemo(
    () => pickupRequests.length > 0 && pickupRequests.every((req: any) => req.selected),
    [pickupRequests],
  );

  // For mobile view, a simple row rendering
  const MobileRow = ({ row, index }: { row: any; index: number }) => (
    <Box key={row.id} p={2} mb={2} border="1px solid #ddd" borderRadius={4} bgcolor="#fff">
      <Typography variant="h6" gutterBottom>
        Pickup Request #{row.id}
      </Typography>
      <Typography variant="body2" gutterBottom>
        <strong>Date:</strong> {row.date}
      </Typography>
      <Typography variant="body2" gutterBottom>
        <strong>Status:</strong> {PICKUP_STATUS_OPTIONS.find((s) => s.value === row.status)?.label}
      </Typography>
      <Typography variant="body2" gutterBottom>
        <strong>Company:</strong> {row.company?.name || 'N/A'}
      </Typography>
      {row.status !== 'DONE' && (
        <Box mt={2}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={() => pickup_request_update(row.id, { status: 'DONE' })}
          >
            Mark as Done
          </Button>
        </Box>
      )}
    </Box>
  );

  return (
    <Grid container spacing={2}>
      {/* Filters Section */}
      <Grid item xs={12}>
        <Box p={2} bgcolor="#f7f9fb" borderRadius={2}>
          <Typography variant="h6" gutterBottom>
            Pickup Request Filters
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={2}>
            <TextField
              type="date"
              label="Date"
              size="small"
              value={filters.date}
              onChange={(e) => setFilters({ ...filters, date: e.target.value })}
              disabled={loading}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              select
              size="small"
              label="Status"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              disabled={loading}
              variant="outlined"
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
            >
              <option value="">All Statuses</option>
              {PICKUP_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </TextField>
            <Button variant="contained" onClick={fetchAPI} disabled={loading}>
              Search
            </Button>
          </Box>
        </Box>
      </Grid>

      {/* Desktop Table */}
      <Grid item xs={12} className="hidden sm:block">
        <MobilePagination
          total={totalCount}
          selected={0}
          loading={loading}
          currentPage={page}
          limit={limit}
          onPageChange={(page) => setPage(page)}
          onLimitChange={(limit) => setLimit(limit)}
          onSortChange={(field) => handleSortChange(field)} // Update sort handler
        />
        <TableContainer component={Paper}>
          <Table>
            <TableHead style={{ backgroundColor: 'lightgray' }}>
              <TableRow>
                <TableCell onClick={() => handleSortChange('id')}>
                  <b>ID</b>
                </TableCell>
                <TableCell onClick={() => handleSortChange('date')}>
                  <b>Date</b>
                </TableCell>
                <TableCell onClick={() => handleSortChange('status')}>
                  <b>Status</b>
                </TableCell>
                <TableCell onClick={() => handleSortChange('company.name')}>
                  <b>Company</b>
                </TableCell>
                <TableCell onClick={() => handleSortChange('action')}>
                  <b>Action</b>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pickupRequests.map((row: any, idx: number) => (
                <TableRow key={row.id}>
                  <TableCell>{row.id}</TableCell>
                  <TableCell>{row.date}</TableCell>
                  <TableCell>{PICKUP_STATUS_OPTIONS.find((s) => s.value === row.status)?.label}</TableCell>
                  <TableCell>{row.company?.name}</TableCell>
                  <TableCell>
                    {row.status !== 'DONE' && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => pickup_request_update(row.id, { status: 'DONE' })}
                      >
                        Mark as Done
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>

      {/* Mobile View */}
      <Grid item xs={12} className="block sm:hidden">
        <MobilePagination
          total={totalCount}
          selected={0}
          loading={loading}
          currentPage={page}
          limit={limit}
          onPageChange={(page) => setPage(page)}
          onLimitChange={(limit) => setLimit(limit)}
          onSortChange={(field) => handleSortChange(field)}
        />
        {pickupRequests.map((row: any, i: number) => (
          <MobileRow row={row} index={i + 1} key={row.id} />
        ))}
        <MobilePagination
          total={totalCount}
          selected={0}
          loading={loading}
          currentPage={page}
          limit={limit}
          onPageChange={(page) => setPage(page)}
          onLimitChange={(limit) => setLimit(limit)}
          onSortChange={(field) => handleSortChange(field)}
        />
      </Grid>

      {/* Action Buttons */}
      <Grid item xs={12}>
        <Box display="flex" justifyContent="flex-end" gap={1}>
          <Button variant="outlined" onClick={fetchAPI} disabled={loading}>
            Refresh
          </Button>
          <Button variant="outlined" onClick={handlePrint}>
            Print
          </Button>
        </Box>
      </Grid>
    </Grid>
  );
}
